import {
	CopyIcon,
	ExternalLinkIcon,
	MousePointer2Icon,
	PanelsTopLeftIcon
} from "lucide-react"
import {
	forwardRef,
	type ComponentProps,
	type ReactNode,
	useRef,
	useState
} from "react"
import { Link as RouterLink } from "react-router-dom"
import { toast } from "sonner"
import {
	ContextMenu,
	ContextMenuContent,
	ContextMenuItem,
	ContextMenuSeparator
} from "~/components/ui/context-menu"

// Keep actual anchors and React Router semantics: modified clicks, relative
// destinations, downloads, refs, and link composition continue to work.
export const Link = forwardRef<
	HTMLAnchorElement,
	ComponentProps<typeof RouterLink> & { nativeContextMenu?: boolean }
>(function Link({ nativeContextMenu, ...props }, ref) {
	return (
		<RouterLink
			ref={ref}
			data-native-context-menu={nativeContextMenu || undefined}
			{...props}
		/>
	)
})

// Delegation also covers plain anchors from rich text, external-link helpers,
// and third-party components without wrapping every anchor in extra DOM.
export function LinkContextMenuProvider({
	children,
	workspace
}: {
	children: ReactNode
	workspace?: {
		open: (path: string) => void
		orgSlug: string
		isMobile: boolean
	}
}) {
	const [link, setLink] = useState<string | null>(null)
	const targetRef = useRef<HTMLAnchorElement | null>(null)
	const nativeLink = useRef<string | null>(null)
	const [open, setOpen] = useState(false)
	const [point, setPoint] = useState({ x: 0, y: 0 })
	const url = link ? new URL(link) : null
	const canOpenInOrbit = !!(
		workspace &&
		!workspace.isMobile &&
		url &&
		url.origin === window.location.origin &&
		url.pathname.startsWith(`/${workspace.orgSlug}/`)
	)

	return (
		<ContextMenu open={open} onOpenChange={setOpen}>
			<div
				className="contents"
				onKeyDown={(event) => {
					if (
						event.defaultPrevented ||
						!(
							event.key === "ContextMenu" ||
							(event.shiftKey && event.key === "F10")
						)
					)
						return
					const anchor =
						event.target instanceof Element
							? event.target.closest<HTMLAnchorElement>("a[href]")
							: null
					if (!anchor) return
					const rect = anchor.getBoundingClientRect()
					// Route keyboard activation through the same opt-outs and URL checks.
					if (
						!anchor.dispatchEvent(
							new MouseEvent("contextmenu", {
								bubbles: true,
								cancelable: true,
								clientX: rect.left,
								clientY: rect.bottom
							})
						)
					)
						event.preventDefault()
				}}
				onContextMenu={(event) => {
					const anchor =
						event.target instanceof Element
							? event.target.closest<HTMLAnchorElement>("a[href]")
							: null
					if (
						event.defaultPrevented ||
						!anchor ||
						anchor.hasAttribute("download") ||
						anchor.closest('[contenteditable="true"]') ||
						anchor.hasAttribute("data-native-context-menu") ||
						!["http:", "https:"].includes(new URL(anchor.href).protocol)
					) {
						event.stopPropagation()
						return
					}
					// Prevent the outer non-workspace provider from opening a second menu.
					event.stopPropagation()
					if (event.shiftKey || nativeLink.current === anchor.href) {
						nativeLink.current = null
						return
					}
					event.preventDefault()
					targetRef.current = anchor
					setLink(anchor.href)
					const rect = anchor.getBoundingClientRect()
					setPoint({
						x: event.clientX || rect.left,
						y: event.clientY || rect.bottom
					})
					setOpen(true)
				}}
			>
				{children}
			</div>
			<ContextMenuContent
				anchor={{
					getBoundingClientRect: () =>
						DOMRect.fromRect({ ...point, width: 0, height: 0 })
				}}
				finalFocus={() => targetRef.current ?? false}
			>
				<ContextMenuItem
					disabled={!canOpenInOrbit}
					onClick={() => {
						if (canOpenInOrbit && url)
							workspace?.open(`${url.pathname}${url.search}${url.hash}`)
					}}
				>
					<PanelsTopLeftIcon /> Open in Orbit tab
				</ContextMenuItem>
				<ContextMenuItem
					onClick={() => {
						if (link) window.open(link, "_blank", "noopener,noreferrer")
					}}
				>
					<ExternalLinkIcon /> Open in browser tab
				</ContextMenuItem>
				<ContextMenuItem
					onClick={() => {
						if (!link) return
						void navigator.clipboard.writeText(link).then(
							() => toast.success("Link copied"),
							() => toast.error("Could not copy link")
						)
					}}
				>
					<CopyIcon /> Copy link
				</ContextMenuItem>
				<ContextMenuSeparator />
				<ContextMenuItem
					onClick={() => {
						nativeLink.current = link
						toast.info(
							"Right-click this link again to open the browser menu. You can also Shift + right-click."
						)
					}}
				>
					<MousePointer2Icon /> Open native context menu
				</ContextMenuItem>
			</ContextMenuContent>
		</ContextMenu>
	)
}
